import React, { useState } from "react";
import { useDispatch } from "react-redux";

import { ExpressCheckoutElement } from "@stripe/react-stripe-js";
import { useStripe, useElements } from "@stripe/react-stripe-js";

import { Checkbox, Row, Col, Spin, Typography } from "antd";

import { openNotificationWithIcon } from "utils/Notification";
import { setSubmitting } from "redux/actions/loading.actions";
import { CheckCircleOutlined, WalletOutlined } from "@ant-design/icons";
import { MERCHANT } from "utils/enums";

const { Text } = Typography;

// Map Stripe's expressPaymentType to your MERCHANT enum.
// Stripe returns one of: 'apple_pay' | 'google_pay' | 'link' | 'amazon_pay' | 'paypal' | 'klarna'
const resolveMerchant = (expressPaymentType) => {
    switch (expressPaymentType) {
        case "apple_pay":
            return MERCHANT.ApplePay;
        case "google_pay":
            return MERCHANT.GooglePay;
        default:
            // Fall back to GooglePay to preserve existing behavior if a new wallet type appears.
            // Consider logging this so you catch unhandled wallet types.
            return MERCHANT.GooglePay;
    }
};

function DigitalWallets({
    setupIntent, onSave, forcePrimary = false, includePrimaryCheckbox = true,
    children, billingDetails
}) {

    const stripe = useStripe();
    const elements = useElements();
    const [isReady, setIsReady] = useState(false);

    const dispatch = useDispatch();

    const [walletStatus, setWalletStatus] = useState("idle"); // idle | loading | success | error
    const [walletMessage, setWalletMessage] = useState("");
    const [isPrimary, setIsPrimary] = useState(forcePrimary);


    const handleWalletConfirm = async (event) => {
        if (!stripe || !elements) {
            console.log("Stripe or elements not loaded");
            return;
        }

        // Stripe passes the wallet type that the user clicked on the event.
        const expressPaymentType = event?.expressPaymentType;
        const merchant = resolveMerchant(expressPaymentType);

        setWalletStatus("loading");
        setWalletMessage("");
        dispatch(setSubmitting(true));

        // Use a wallet-agnostic return path. If your backend / callback page
        // keys off "confirm-gpay", either keep it for backward compatibility
        // or rename to "/confirm-wallet" and update the handler.
        const returnUrl = window.location.href + "/confirm-wallet";

        try {
            const { error, setupIntent: confirmedSetupIntent } = await stripe.confirmSetup({
                elements,
                redirect: "if_required",
                confirmParams: {
                    return_url: returnUrl,
                    payment_method_data: {
                        billing_details: billingDetails,
                        metadata: {
                            isPrimary,
                            walletType: expressPaymentType,
                        }
                    }
                },
            });

            if (error) {
                setWalletStatus("error");
                setWalletMessage(error.message);
                openNotificationWithIcon("error", "Error!", error.message);
                dispatch(setSubmitting(false));
                return;
            }

            // Success - payment method was created
            if (confirmedSetupIntent?.payment_method) {
                const isCardPrimary = isPrimary; // persist the value
                onSave?.(confirmedSetupIntent.payment_method, isCardPrimary, merchant);
            }
        } catch (err) {
            console.error("Wallet confirm error:", err);
            setWalletStatus("error");
            setWalletMessage("An error occurred. Please try again.");
            openNotificationWithIcon("error", "Error!", "An error occurred while processing your payment.");
        } finally {
            dispatch(setSubmitting(false));
        }
    };

    const onWalletLoadError = ({ error }) => {
        setWalletStatus("error");
        setWalletMessage(error.message || "No digital wallets are available in this browser.");
    };

    return <div style={{ marginTop: 24 }}>
        <Row>
            <Col xs={24} md={children ? 12 : 24}>
                <div style={{
                    backgroundColor: "#486baf09",
                    border: "1px solid #D9D9D9",
                    borderRadius: 8,
                    padding: 20,
                    marginBottom: 16,
                    textAlign: "center"
                }}>
                    <div style={{ marginBottom: 8 }}>
                        <div style={{
                            height: 32, width: 32, display: "flex", justifyContent: "center", alignItems: "center", backgroundColor: '#000',
                            borderRadius: 6, margin: "0 auto"
                        }}>
                            <WalletOutlined style={{ color: '#fff', fontSize: 20 }} />
                        </div>
                    </div>
                    <div style={{
                        marginBottom: 8, fontSize: 16, fontWeight: 600, color: "#15213B",
                        fontFamily: 'tradeGothic'
                    }}>
                        Digital Wallets
                    </div>
                    <div style={{
                        fontSize: 14, color: "#7F889A", marginBottom: 16,
                        fontFamily: 'tradeGothic'
                    }}>
                        Pay with Apple Pay or Google Pay — saved securely via Stripe. Options shown depend on your device.
                    </div>
                </div>

                {setupIntent && (
                    <div style={{ marginBottom: 16 }}>
                        <ExpressCheckoutElement
                            onReady={() => setIsReady(true)}
                            onConfirm={handleWalletConfirm}
                            onLoadError={onWalletLoadError}
                            options={{
                                buttonType: {
                                    googlePay: "plain"
                                },
                                wallets: {
                                    googlePay: "always",
                                    applePay: "always",
                                    link: "never",
                                },
                                paymentMethods: {
                                    googlePay: "always",
                                    applePay: "always",
                                    link: "never",
                                },
                                layout: {
                                    maxRows: 2,
                                    maxColumns: 1
                                }

                            }}

                        />
                        {
                            !isReady && <Spin style={{ display: "block", margin: "20px auto" }} />
                        }
                    </div>
                )}

                {walletStatus === "loading" && (
                    <div style={{ textAlign: "center", padding: 20 }}>
                        <Spin />
                        <Text style={{ marginLeft: 12 }}>Processing...</Text>
                    </div>
                )}

                {walletStatus === "success" && (
                    <div style={{
                        backgroundColor: "#e8f5e9",
                        color: "#388e3c",
                        padding: 12,
                        borderRadius: 8,
                        marginTop: 12,
                        textAlign: "center"
                    }}>
                        <CheckCircleOutlined style={{ marginRight: 8 }} />
                        {walletMessage}
                    </div>
                )}

                {walletStatus === "error" && (
                    <div style={{
                        backgroundColor: "#ffebee",
                        color: "#d32f2f",
                        padding: 12,
                        borderRadius: 8,
                        marginTop: 12,
                        textAlign: "center"
                    }}>
                        {walletMessage}
                    </div>
                )}

                {
                    includePrimaryCheckbox && <div style={{ marginTop: 16 }}>
                        <Checkbox
                            name='isPrimary'
                            className='checkboxRegular'
                            formItemStyles={{ marginBottom: 0, display: 'inline-flex' }}
                            checked={isPrimary}
                            onChange={() => setIsPrimary(checked => !checked)}
                        >
                            Set as Primary
                        </Checkbox>
                    </div>
                }
            </Col>
        </Row>

        {children}

        <div style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 16,
            fontSize: 12,
            color: "#7F889A"
        }}>
            <svg viewBox="0 0 24 24" width="16" height="16" style={{ color: "#486baf" }}>
                <path
                    d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"
                    fill="currentColor"
                />
            </svg>
            Secured by Stripe. Your data is encrypted.
        </div>
    </div>;
}

export default DigitalWallets;